// Shared upload preflight: given an array of rows that may carry a
// supplier_name, return whether every value is an active supplier in
// the canonical /suppliers table. Used by the Products and Manifest
// XLSX upload flows to prompt the operator before storing rows that
// reference unknown / archived suppliers.
//
// Always fetches a fresh /suppliers list (status=all) at call time
// rather than relying on a cached FE state — closes the staleness
// window where another admin just created the supplier the operator
// is referencing.

import { apiFetch } from "../api/apiFetch";

export interface PreflightInput {
    supplier_name?: string | null;
}

export type PreflightResult =
    | { ok: true }
    | { ok: false; missing: string[]; archived: string[] };

interface SuppliersResponse {
    suppliers: Array<{ name: string; archived_at?: string | null }>;
}

export const preflightSuppliers = async (rows: PreflightInput[]): Promise<PreflightResult> => {
    const wanted = new Set<string>();
    for (const r of rows) {
        const v = (r.supplier_name ?? "").trim();
        if (v) wanted.add(v);
    }
    if (wanted.size === 0) return { ok: true };

    const res = await apiFetch(`/suppliers?status=all&page_size=500`, {});
    if (!res.ok) throw new Error(`preflight fetch failed: ${res.status}`);
    const data: SuppliersResponse = await res.json();
    const status = new Map<string, "active" | "archived">();
    for (const s of data.suppliers ?? []) {
        status.set(s.name, s.archived_at ? "archived" : "active");
    }

    const missing: string[] = [];
    const archived: string[] = [];
    for (const name of wanted) {
        const s = status.get(name);
        if (s === undefined) missing.push(name);
        else if (s === "archived") archived.push(name);
    }
    if (missing.length === 0 && archived.length === 0) return { ok: true };
    missing.sort();
    archived.sort();
    return { ok: false, missing, archived };
};

// Build the /suppliers/bulk row set that auto-creates missing
// suppliers and restores any archived ones to active. Caller POSTs
// this to /suppliers/bulk before continuing the original upload.
export const buildSupplierFixupRows = (missing: string[], archived: string[]): Record<string, string>[] => [
    ...missing.map(name => ({ action: "add", name })),
    ...archived.map(name => ({ action: "update", name, status: "active" })),
];
